/**
 * Scoped `Document.EntityData` comprehension emission — the shared primitives behind the
 * `UseScopedEntityData` rollout flag.
 *
 * The legacy path projects created records into `FormData._EntityData` as a flat
 * `[Entity][GUID]` ledger and lets the browser-side `FormEntityCreator` apply it. The scoped
 * path writes each record straight into the document's own `EntityData` under a trigger-context
 * key — `OnSave.<Entity>.<GUID>`, `OnEnteringState.Approved.<Entity>.<GUID>` — which the server
 * applies (api/headlight on document update, approvals on approval action). WHEN a record is
 * applied is encoded ONLY by which context it is nested under, so scheduling is configuration
 * (`PictForm.ComprehensionContext`), not provider code.
 *
 * Writes route through `DynamicFormSolverBehaviors.addComprehensionEntity` — the one shared
 * comprehension writer, also used by the solver path and `DocumentDynamicSectionManager` — so
 * every producer emits the same shape.
 *
 * @module _ScopedEntityData
 */

/** The context a save-time record lands under when an input declares none. */
const DEFAULT_CONTEXT = 'OnSave';

/**
 * The context a deferred value lands under when an input declares none.
 *
 * Keyed on the status the document enters rather than the action that took it there: a workflow whose
 * every step is `Approve` applies an action-keyed value at the FIRST step and again at each one after
 * it. Requires an API and approvals service carrying `OnEnteringState` (0.43.162 or later); an older
 * one strips the deferred value with the rest of the trigger's deny-listed columns.
 */
const DEFAULT_DEFERRED_CONTEXT = 'OnEnteringState.Approved';

/**
 * The trigger keys a comprehension nests under, and how many address parts each takes before the
 * entity buckets begin. `OnSave` stages for the next document save and is drained once applied; the
 * others stage for a workflow transition, name the action or status they fire on, and stand until
 * that transition happens — so a leaf under one of them outlives many saves.
 *
 * Every walk of the tree reads this rather than naming the keys it knows, so adding a trigger is one
 * entry here. Mirrors `SPECIAL_TABLES_KEYS` in `headlight-comprehension-processor` and its copy in
 * `api/headlight`.
 */
const TRIGGER_CONTEXT_DEPTH = Object.freeze({ OnSave: 1, OnApprovalAction: 2, OnEnteringState: 2 });

/** The trigger keys that stage for a workflow transition rather than for the next save. */
const DEFERRED_TRIGGERS = Object.freeze([ 'OnApprovalAction', 'OnEnteringState' ]);

/**
 * The deprecated deferred trigger. The server still applies it, so documents already carrying one keep
 * working; a form that stages under another trigger takes its leaves with it rather than leaving a
 * second copy that applies on the first approval step.
 */
const LEGACY_DEFERRED_TRIGGER = 'OnApprovalAction';

/** Fallback comprehension destination when the solver-behaviors provider is unavailable. */
const DOCUMENT_ENTITY_DATA_ADDRESS = 'AppData.DocumentData.ReportData.EntityData';

/**
 * Read one `UseScopedEntityData` setting. Strings are honored because the value is authored in
 * JSON form config, where a boolean often arrives quoted — and an opt-out that silently failed to
 * take would leave a form on the legacy path with no signal.
 *
 * @param {any} pValue - The configured value.
 * @return {boolean|null} - The setting, or null when the value expresses no opinion.
 */
function readFlagValue(pValue)
{
	if (typeof pValue === 'boolean')
	{
		return pValue;
	}
	if (typeof pValue === 'string')
	{
		const tmpNormalized = pValue.trim().toLowerCase();
		if (tmpNormalized === 'false')
		{
			return false;
		}
		if (tmpNormalized === 'true')
		{
			return true;
		}
	}
	return null;
}

/**
 * Whether the scoped-emission path is active. ONE flag drives both the inputs' emission target and
 * the `FormEntityCreator` skip — the two paths must never run together, so this resolves from a
 * single name: the form manifest wins (either direction) over the app-level setting. Scoped emission
 * is the DEFAULT; only an explicit `false` holds a form on the legacy
 * `FormData._EntityData` + browser-side-apply path.
 *
 * Default-on is a QA-shakedown posture, not a prod decision. It is safe here only because the
 * server-side apply now archives an applied `OnSave` subtree into `EntityData.ExecutionLog` and
 * guards re-application on a content hash; without that, a stored subtree replays on EVERY Document
 * update and reverts whatever wrote to those entities since the last form save. A branch build cut
 * for stage or release picks this default up, so the prod posture is a decision that has to be taken
 * before that cut, not inherited from this line.
 *
 * @param {Object} pPict - The pict instance.
 * @return {boolean} - True when inputs should emit scoped comprehensions and FEC should stand down.
 */
function isEnabled(pPict)
{
	const tmpSettings = (pPict && pPict.settings) || { };
	const tmpManifest = tmpSettings.MyDefaultFormManifest;
	const tmpManifestFlag = readFlagValue(tmpManifest && tmpManifest.UseScopedEntityData);
	if (tmpManifestFlag !== null)
	{
		return tmpManifestFlag;
	}
	const tmpApplicationFlag = readFlagValue(tmpSettings.UseScopedEntityData);
	if (tmpApplicationFlag !== null)
	{
		return tmpApplicationFlag;
	}
	return true;
}

/**
 * The pict flag an input sets once it has actually emitted scoped, and `FormEntityCreator` reads
 * before running its legacy apply.
 *
 * The two paths must never run over the same save. Both sides resolving `isEnabled` is not enough
 * on its own: they agree only if they are the SAME module, and a second installed copy of this
 * bundle (or a stale build of one half) puts them back out of step with no signal — the ledger
 * reads empty, and the diff-delete takes that for "every document-owned record was removed".
 * This is the runtime cross-check for that.
 */
const ACTIVE_MARKER = '_ScopedEntityDataActive';

/**
 * Record that an input emitted scoped on this save.
 *
 * @param {Object} pPict - The pict instance.
 */
function markActive(pPict)
{
	if (pPict && typeof pPict === 'object')
	{
		pPict[ACTIVE_MARKER] = true;
	}
}

/**
 * Whether an input has emitted scoped comprehensions on this pict.
 *
 * @param {Object} pPict - The pict instance.
 * @return {boolean} - True when the scoped path is live.
 */
function isMarkedActive(pPict)
{
	return !!(pPict && typeof pPict === 'object' && pPict[ACTIVE_MARKER]);
}

/**
 * Context roots the picker family projected into on this save, kept on the pict rather than on a
 * provider: every Materials provider's save hook can drive a sweep, and the provider that prunes last
 * must also prune what other providers' inputs projected, or its sweep re-sends their records whole.
 */
const NOTED_PROJECTION_ROOTS_MARKER = '_ScopedEntityDataNotedProjectionRoots';

/**
 * Note a context root an input projected into, for whichever provider sanitizes after the sweep.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {Record<string, any>} pContextRoot - The root the input wrote into.
 */
function noteProjectionRoot(pPict, pContext, pContextRoot)
{
	if (!pPict || typeof pPict !== 'object' || !pContextRoot || typeof pContextRoot !== 'object')
	{
		return;
	}
	if (!(pPict[NOTED_PROJECTION_ROOTS_MARKER] instanceof Map))
	{
		pPict[NOTED_PROJECTION_ROOTS_MARKER] = new Map();
	}
	pPict[NOTED_PROJECTION_ROOTS_MARKER].set(pContext, pContextRoot);
}

/**
 * Take (and clear) the context roots noted since the last sanitize pass.
 *
 * @param {Object} pPict - The pict instance.
 * @return {Array<[string, Record<string, any>]>} - `[context, root]` pairs; empty when none were noted.
 */
function takeNotedProjectionRoots(pPict)
{
	if (!pPict || typeof pPict !== 'object' || !(pPict[NOTED_PROJECTION_ROOTS_MARKER] instanceof Map))
	{
		return [];
	}
	const tmpNoted = [ ...pPict[NOTED_PROJECTION_ROOTS_MARKER].entries() ];
	pPict[NOTED_PROJECTION_ROOTS_MARKER].clear();
	return tmpNoted;
}

/**
 * Resolve the trigger context an input emits under.
 *
 * @param {Object} pInput - The form input descriptor.
 * @param {string} [pDefaultContext] - The provider's default when the input declares none.
 * @return {string} - The comprehension context (a dotted address, e.g. `OnApprovalAction.Approve`).
 */
function resolveContext(pInput, pDefaultContext)
{
	const tmpConfigured = pInput && pInput.PictForm && pInput.PictForm.ComprehensionContext;
	if (typeof tmpConfigured === 'string' && tmpConfigured.length > 0)
	{
		return tmpConfigured;
	}
	return pDefaultContext || DEFAULT_CONTEXT;
}

/**
 * Field-name prefixes that mark a meadow directive rather than a column. `_Dest_` composes with the
 * field it redirects, so the destination directive for an exact-GUID reference is `_Dest_` +
 * `_GUID<Entity>` — `_Dest__GUID<Entity>`, a double underscore the transient-state test would
 * otherwise eat, silently retargeting the foreign key.
 */
const MEADOW_DIRECTIVE_PREFIXES = [ '_Dest_', '_GUID' ];

/**
 * Is a field transient entity-selector UI state rather than something to persist?
 *
 * Selectors marshal `<Field>__Salt` / `<Field>__Record` siblings alongside their value; carried into
 * a comprehension they reach meadow's INSERT column list and diverge the column/value counts.
 *
 * @param {string} pFieldName - The record field name.
 * @return {boolean} - True when the field is UI state and must not be persisted.
 */
function isTransientFormField(pFieldName)
{
	if (typeof pFieldName !== 'string' || !pFieldName.includes('__'))
	{
		return false;
	}
	for (const tmpPrefix of MEADOW_DIRECTIVE_PREFIXES)
	{
		if (pFieldName.startsWith(tmpPrefix))
		{
			return false;
		}
	}
	return true;
}

/**
 * Strip transient entity-selector UI state from a record before it is persisted. Mirrors
 * `FormEntityCreator.sanitizeComprehension` at the record level.
 *
 * @param {Record<string, any>} pRecord - The record to sanitize.
 * @return {Record<string, any>} - A clone with the UI-state fields removed.
 */
/**
 * Whether a field is an id column carrying a value no id column can hold.
 *
 * The selectors use a placeholder for "the user asked for a new one" (`__NEW__`) and put it in the
 * record's own id slot until the server resolves a real one. It is interface state, not data: an
 * upsert resolves the row by GUID and then compares the id it found against the id the record
 * carries, so a placeholder that outlives the create makes every later save of that record fail with
 * `Default identifier does not match GUID record` - permanently, because the row now exists.
 *
 * Only strings that are not numbers at all are dropped. `0`, `''` and `'57004'` are left exactly as
 * they were: they reach the column as a number, and changing that would change what a save means.
 *
 * @param {string} pFieldName - the field name.
 * @param {any} pValue - the field value.
 * @return {boolean} - True when the field must not be emitted.
 */
function isUnresolvedIdentityField(pFieldName, pValue)
{
	if (typeof pFieldName !== 'string' || !(/^ID[A-Z]/).test(pFieldName))
	{
		return false;
	}
	if (typeof pValue !== 'string')
	{
		return false;
	}
	return !Number.isFinite(Number(pValue));
}

function sanitizeRecord(pRecord)
{
	const tmpSanitized = { };
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return tmpSanitized;
	}
	for (const tmpField of Object.keys(pRecord))
	{
		if (isTransientFormField(tmpField))
		{
			continue;
		}
		if (isUnresolvedIdentityField(tmpField, pRecord[tmpField]))
		{
			continue;
		}
		tmpSanitized[tmpField] = pRecord[tmpField];
	}
	return tmpSanitized;
}

/**
 * The pict-level marker naming the scratch tree a baseline sweep is collecting into.
 *
 * The baseline is captured by re-running the ordinary save sweep at load, so every projector — both
 * input families, the subclass cascades, the deferred TestResult split — must land in the scratch
 * rather than in the document. Redirecting the single destination resolver is what achieves that:
 * everything downstream (context roots, entity buckets, record writes) already routes through it.
 */
const BASELINE_SWEEP_MARKER = '_ScopedEntityDataBaselineSweep';

/**
 * Begin a baseline sweep: subsequent comprehension writes land in a fresh scratch tree instead of
 * the document's own EntityData.
 *
 * @param {Object} pPict - The pict instance.
 * @return {Record<string, any>} - The scratch tree the sweep will fill.
 */
function beginBaselineSweep(pPict)
{
	const tmpSweep = { Scratch: { }, Contexts: new Map() };
	if (pPict && typeof pPict === 'object')
	{
		pPict[BASELINE_SWEEP_MARKER] = tmpSweep;
	}
	return tmpSweep;
}

/**
 * The in-flight baseline sweep, or null when none is running.
 *
 * @param {Object} pPict - The pict instance.
 * @return {{ Scratch: Record<string, any>, Contexts: Map<string, Record<string, any>> }|null} - The sweep, or null.
 */
function getBaselineSweepTarget(pPict)
{
	if (!pPict || typeof pPict !== 'object')
	{
		return null;
	}
	const tmpSweep = pPict[BASELINE_SWEEP_MARKER];
	return (tmpSweep && typeof tmpSweep === 'object' && tmpSweep.Scratch) ? tmpSweep : null;
}

/**
 * End a baseline sweep and hand back what it collected, as the (context -> context root) pairs the
 * projectors actually resolved. The pairs are what the caller needs: a context is a DOTTED address
 * (`OnApprovalAction.Approve`), so the collected tree cannot be walked back into contexts and entity
 * buckets by shape alone.
 *
 * @param {Object} pPict - The pict instance.
 * @return {Map<string, Record<string, any>>|null} - The collected roots by context, or null when no sweep was running.
 */
function endBaselineSweep(pPict)
{
	const tmpSweep = getBaselineSweepTarget(pPict);
	if (pPict && typeof pPict === 'object')
	{
		delete pPict[BASELINE_SWEEP_MARKER];
	}
	return tmpSweep ? tmpSweep.Contexts : null;
}

/**
 * Resolve the object comprehensions are written into. Prefers the solver-behaviors provider's own
 * destination (configured on the metacontroller as `comprehensionDestinationAddress`, which the
 * configurable application points at `Document.EntityData`) so scoped writes and solver writes
 * always land in the same place.
 *
 * @param {Object} pPict - The pict instance.
 * @return {Record<string, any>|null} - The destination object, or null when none can be resolved.
 */
function resolveDestination(pPict)
{
	const tmpBaselineSweep = getBaselineSweepTarget(pPict);
	if (tmpBaselineSweep)
	{
		return tmpBaselineSweep.Scratch;
	}
	const tmpBehaviors = pPict && pPict.providers && pPict.providers.DynamicFormSolverBehaviors;
	if (tmpBehaviors && typeof tmpBehaviors.resolveComprehensionDestination === 'function')
	{
		const tmpResolved = tmpBehaviors.resolveComprehensionDestination();
		if (tmpResolved && typeof tmpResolved === 'object' && !Array.isArray(tmpResolved))
		{
			return tmpResolved;
		}
	}
	if (!pPict || !pPict.manifest || typeof pPict.manifest.getValueAtAddress !== 'function')
	{
		return null;
	}
	const tmpEntityData = pPict.manifest.getValueAtAddress(pPict, DOCUMENT_ENTITY_DATA_ADDRESS);
	if (tmpEntityData && typeof tmpEntityData === 'object' && !Array.isArray(tmpEntityData))
	{
		return tmpEntityData;
	}
	if (typeof pPict.manifest.setValueAtAddress !== 'function')
	{
		return null;
	}
	const tmpCreated = { };
	pPict.manifest.setValueAtAddress(pPict, DOCUMENT_ENTITY_DATA_ADDRESS, tmpCreated);
	return tmpCreated;
}

/**
 * Walk (creating as it goes) `pNode -> <context parts>` and hand back the node the entity buckets
 * hang from.
 *
 * @param {Record<string, any>} pNode - The node to walk from.
 * @param {string} pContext - The dotted context address.
 * @return {Record<string, any>} - The context node.
 */
function walkContext(pNode, pContext)
{
	let tmpCursor = pNode;
	for (const tmpPart of String(pContext).split('.'))
	{
		if (tmpPart.length < 1)
		{
			continue;
		}
		if (!tmpCursor[tmpPart] || typeof tmpCursor[tmpPart] !== 'object' || Array.isArray(tmpCursor[tmpPart]))
		{
			tmpCursor[tmpPart] = { };
		}
		tmpCursor = tmpCursor[tmpPart];
	}
	return tmpCursor;
}

/**
 * The node a context's records are keyed under, as `<Entity>.<GUID>`.
 *
 * That is the SAME shape as the legacy flat `_EntityData` ledger, which is what makes the scoped
 * conversion a retarget rather than a rewrite: hand this node to a projector written against the
 * flat ledger and it produces a correct scoped comprehension, unchanged.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The dotted context address.
 * @return {Record<string, any>|null} - The context root, or null when no destination resolves.
 */
function resolveContextRoot(pPict, pContext)
{
	const tmpDestination = resolveDestination(pPict);
	if (!tmpDestination)
	{
		return null;
	}
	const tmpContext = pContext || DEFAULT_CONTEXT;
	const tmpRoot = walkContext(tmpDestination, tmpContext);
	const tmpBaselineSweep = getBaselineSweepTarget(pPict);
	if (tmpBaselineSweep)
	{
		tmpBaselineSweep.Contexts.set(tmpContext, tmpRoot);
	}
	return tmpRoot;
}

/**
 * Walk (creating as it goes) `Destination -> <context parts> -> <Entity>` and hand back the
 * entity bucket.
 *
 * @param {Record<string, any>} pDestination - The comprehension destination object.
 * @param {string} pContext - The dotted context address.
 * @param {string} pEntity - The entity name.
 * @return {Record<string, any>} - The entity bucket, keyed by external GUID.
 */
function resolveEntityBucket(pDestination, pContext, pEntity)
{
	const tmpCursor = walkContext(pDestination, pContext);
	if (!tmpCursor[pEntity] || typeof tmpCursor[pEntity] !== 'object' || Array.isArray(tmpCursor[pEntity]))
	{
		tmpCursor[pEntity] = { };
	}
	return tmpCursor[pEntity];
}

/**
 * Replace every `<Entity>.<GUID>` leaf under a context root with a sanitized CLONE.
 *
 * The projectors key the form's LIVE record objects into the comprehension by reference, and this
 * tree is saved with the document — so the transient `__` UI state has to come off, and it has to
 * come off a copy, because the form goes on rendering from the original. This is the scoped
 * counterpart of the pass `FormEntityCreator` used to run over the ledger at apply time.
 *
 * @param {Record<string, any>} pContextRoot - The context root to sanitize in place.
 * @return {boolean} - True when a root was walked.
 */
function sanitizeContextRoot(pContextRoot)
{
	if (!pContextRoot || typeof pContextRoot !== 'object' || Array.isArray(pContextRoot))
	{
		return false;
	}
	for (const tmpEntity of Object.keys(pContextRoot))
	{
		const tmpBucket = pContextRoot[tmpEntity];
		if (!tmpBucket || typeof tmpBucket !== 'object' || Array.isArray(tmpBucket))
		{
			continue;
		}
		for (const tmpGUID of Object.keys(tmpBucket))
		{
			const tmpRecord = tmpBucket[tmpGUID];
			if (!tmpRecord || typeof tmpRecord !== 'object' || Array.isArray(tmpRecord))
			{
				continue;
			}
			tmpBucket[tmpGUID] = sanitizeRecord(tmpRecord);
		}
	}
	return true;
}

/**
 * Write one record into `<Context>.<Entity>.<GUID>`, replacing whatever leaf was there.
 *
 * The leaf is dropped before the write because `addComprehensionEntity` ACCUMULATES properties on
 * an existing (Context, Entity, GUID) — so a re-added record would otherwise inherit the `Deleted`
 * flag from a tombstone, or fields the form has since dropped.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @param {Record<string, any>} pRecord - The record to write (sanitized here).
 * @return {boolean} - True when the record was written.
 */
function writeRecord(pPict, pContext, pEntity, pGUID, pRecord)
{
	if (!pContext || !pEntity || !pGUID)
	{
		return false;
	}
	const tmpDestination = resolveDestination(pPict);
	if (!tmpDestination)
	{
		return false;
	}
	const tmpBucket = resolveEntityBucket(tmpDestination, pContext, pEntity);
	delete tmpBucket[pGUID];

	const tmpSanitized = sanitizeRecord(pRecord);
	const tmpBehaviors = pPict && pPict.providers && pPict.providers.DynamicFormSolverBehaviors;
	if (tmpBehaviors && typeof tmpBehaviors.addComprehensionEntity === 'function' && !getBaselineSweepTarget(pPict))
	{
		for (const tmpField of Object.keys(tmpSanitized))
		{
			tmpBehaviors.addComprehensionEntity(pContext, pEntity, pGUID, tmpField, tmpSanitized[tmpField]);
		}
		return true;
	}
	tmpBucket[pGUID] = tmpSanitized;
	return true;
}

/**
 * Mint a tombstone: the minimal record the server-side pre-pass needs to resolve the row by GUID
 * and delete it. The safety property is the ORDERING on the apply path, not the adapter:
 * `applyComprehensionSubtree` runs `partitionTombstones` first and splits every `Deleted`-carrying
 * record out of the upsert subtree, so this leaf never reaches the adapter as an update. The
 * adapter itself does NOT strip `Deleted` -- it honours it as a delete, and `PerformDeletes`
 * defaults on -- so a tombstone routed anywhere that skips the partition pass still deletes.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @return {boolean} - True when the tombstone was written.
 */
function writeTombstone(pPict, pContext, pEntity, pGUID)
{
	const tmpRecord = { Deleted: 1 };
	tmpRecord[`GUID${pEntity}`] = pGUID;
	return writeRecord(pPict, pContext, pEntity, pGUID, tmpRecord);
}

/**
 * Whether a record is known to exist server-side, so a tombstone for it cannot delete a phantom.
 * Evidence, in order: a server-assigned `ID<Entity>` on the record itself; the legacy root ledger
 * a `FormEntityCreator`-era save left behind; a leaf a prior scoped save wrote under this context.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context the record is emitted under.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @param {Record<string, any>} [pRecord] - (optional) The in-memory record.
 * @return {boolean} - True when the record has been persisted.
 */
function isPersistedGUID(pPict, pContext, pEntity, pGUID, pRecord)
{
	if (!pGUID)
	{
		return false;
	}
	if (pRecord && typeof pRecord === 'object' && pRecord[`ID${pEntity}`])
	{
		return true;
	}
	// A save folds every record it emitted into the baseline. Offline that is the only evidence there
	// is: no response comes back, so no row id is ever written onto the record, and the delta prune
	// takes the leaf out of the tree as soon as a later save finds it unchanged.
	if (pPict && pPict.manifest && typeof pPict.manifest.getValueAtAddress === 'function')
	{
		const tmpBaseline = pPict.manifest.getValueAtAddress(pPict, ENTITY_DATA_BASELINE_ADDRESS);
		const tmpBaselineContext = (tmpBaseline && typeof tmpBaseline === 'object') ? tmpBaseline[pContext || DEFAULT_CONTEXT] : null;
		const tmpBaselineBucket = (tmpBaselineContext && typeof tmpBaselineContext === 'object') ? tmpBaselineContext[pEntity] : null;
		if (tmpBaselineBucket && typeof tmpBaselineBucket === 'object' && tmpBaselineBucket[pGUID])
		{
			return true;
		}
	}
	const tmpDestination = resolveDestination(pPict);
	if (!tmpDestination)
	{
		return false;
	}
	const tmpRootBucket = tmpDestination[pEntity];
	if (tmpRootBucket && typeof tmpRootBucket === 'object' && tmpRootBucket[pGUID])
	{
		return true;
	}
	let tmpCursor = tmpDestination;
	for (const tmpPart of String(pContext || DEFAULT_CONTEXT).split('.'))
	{
		if (tmpPart.length < 1)
		{
			continue;
		}
		tmpCursor = tmpCursor[tmpPart];
		if (!tmpCursor || typeof tmpCursor !== 'object')
		{
			return false;
		}
	}
	const tmpScopedBucket = tmpCursor[pEntity];
	return !!(tmpScopedBucket && typeof tmpScopedBucket === 'object' && tmpScopedBucket[pGUID]);
}

/**
 * The document record the form is editing. `DOCUMENT_ENTITY_DATA_ADDRESS` is this plus `.EntityData`.
 */
const DOCUMENT_RECORD_ADDRESS = 'AppData.DocumentData.ReportData';

/**
 * Where the delta baseline rides. Inside `FormData` so it survives an offline save and reload;
 * `_EntityDataBaseline` owns the contents and reads the address from here so there is one of it.
 */
const ENTITY_DATA_BASELINE_ADDRESS = `${DOCUMENT_RECORD_ADDRESS}.FormData.__EntityDataBaseline`;

/**
 * The pict-level counter marking a PROGRAMMATIC value write — a load, an adoption, a cascade — as
 * opposed to a user edit. It is deliberately the same name the autofill-scope vet already suppresses
 * on, so a load driven by either input family is seen as a load by both.
 */
const PROGRAMMATIC_LOAD_MARKER = '_MaterialsEntityHydrationDepth';

/**
 * Mark the start of a programmatic value write. `pict-section-form`'s `setDataByInput` routes
 * straight into `onDataChange`, so a loader that seeds a value is indistinguishable from a user
 * edit at the provider seam — and the removal routes read exactly that seam. Pair with
 * `endProgrammaticLoad` in a `finally`.
 *
 * @param {Object} pPict - The pict instance.
 */
function beginProgrammaticLoad(pPict)
{
	if (pPict && typeof pPict === 'object')
	{
		pPict[PROGRAMMATIC_LOAD_MARKER] = (pPict[PROGRAMMATIC_LOAD_MARKER] || 0) + 1;
	}
}

/**
 * Close a `beginProgrammaticLoad` scope.
 *
 * @param {Object} pPict - The pict instance.
 */
function endProgrammaticLoad(pPict)
{
	if (pPict && typeof pPict === 'object' && pPict[PROGRAMMATIC_LOAD_MARKER] > 0)
	{
		pPict[PROGRAMMATIC_LOAD_MARKER]--;
	}
}

/**
 * Whether a programmatic value write is in flight.
 *
 * @param {Object} pPict - The pict instance.
 * @return {boolean} - True while a load, adoption or cascade is driving the value.
 */
function isProgrammaticLoad(pPict)
{
	return !!(pPict && typeof pPict === 'object' && pPict[PROGRAMMATIC_LOAD_MARKER]);
}

/**
 * Drop every staged leaf for one record, in every trigger context.
 *
 * A record can be staged under more than one context — a TestInstance saves under `OnSave` while its
 * `TestResult` waits under `OnEnteringState.Approved`. Tombstoning it in one context alone would leave
 * the other leaf to be applied later as an UPSERT, and an upsert creates: the row the user removed
 * would come back at approval time. The approvals path has no owner GUID, so its tombstones are
 * skipped wholesale — leaving the leaf is the only thing that can go wrong there, so it must go.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @return {number} - How many leaves were dropped.
 */
function dropRecordLeaves(pPict, pEntity, pGUID)
{
	const tmpDestination = resolveDestination(pPict);
	if (!tmpDestination || !pEntity || !pGUID)
	{
		return 0;
	}
	let tmpDropped = 0;
	forEachContextRoot(tmpDestination, (pContextRoot) =>
	{
		const tmpBucket = pContextRoot[pEntity];
		if (tmpBucket && typeof tmpBucket === 'object' && !Array.isArray(tmpBucket) && Object.prototype.hasOwnProperty.call(tmpBucket, pGUID))
		{
			delete tmpBucket[pGUID];
			tmpDropped++;
		}
	});
	if (tmpDropped > 0)
	{
		scrubEmptyContainers(tmpDestination);
	}
	return tmpDropped;
}

/**
 * Walk every node entity buckets hang from: each trigger context, and the destination root itself,
 * which carries the legacy flat ledger.
 *
 * @param {Record<string, any>} pDestination - The comprehension destination.
 * @param {(pContextRoot: Record<string, any>, pTrigger: string|null, pContextKey: string|null) => void} fCallback - Called per node.
 * @return {void}
 */
function forEachContextRoot(pDestination, fCallback)
{
	if (!pDestination || typeof pDestination !== 'object' || Array.isArray(pDestination))
	{
		return;
	}
	fCallback(pDestination, null, null);
	for (const tmpTrigger of Object.keys(TRIGGER_CONTEXT_DEPTH))
	{
		const tmpTriggerNode = pDestination[tmpTrigger];
		if (!tmpTriggerNode || typeof tmpTriggerNode !== 'object' || Array.isArray(tmpTriggerNode))
		{
			continue;
		}
		if (TRIGGER_CONTEXT_DEPTH[tmpTrigger] === 1)
		{
			fCallback(tmpTriggerNode, tmpTrigger, null);
			continue;
		}
		for (const tmpContextKey of Object.keys(tmpTriggerNode))
		{
			const tmpContextRoot = tmpTriggerNode[tmpContextKey];
			if (tmpContextRoot && typeof tmpContextRoot === 'object' && !Array.isArray(tmpContextRoot))
			{
				fCallback(tmpContextRoot, tmpTrigger, tmpContextKey);
			}
		}
	}
}

/**
 * Drop the containers a removal emptied: an entity bucket holding no records, a context holding no
 * entities, a trigger holding no contexts. An empty bucket applies nothing, but it reads as staged
 * intent to anything that walks the tree, and it rides every save and every sync.
 *
 * The destination root is left alone — its own keys are the triggers, the execution log and the
 * legacy ledger, none of which this may prune.
 *
 * @param {Record<string, any>} pDestination - The comprehension destination.
 * @return {void}
 */
function scrubEmptyContainers(pDestination)
{
	if (!pDestination || typeof pDestination !== 'object' || Array.isArray(pDestination))
	{
		return;
	}
	for (const tmpTrigger of Object.keys(TRIGGER_CONTEXT_DEPTH))
	{
		const tmpTriggerNode = pDestination[tmpTrigger];
		if (!tmpTriggerNode || typeof tmpTriggerNode !== 'object' || Array.isArray(tmpTriggerNode))
		{
			continue;
		}
		const tmpContexts = (TRIGGER_CONTEXT_DEPTH[tmpTrigger] === 1) ? [ [ null, tmpTriggerNode ] ] : Object.entries(tmpTriggerNode);
		for (const [ tmpContextKey, tmpContextRoot ] of tmpContexts)
		{
			if (!tmpContextRoot || typeof tmpContextRoot !== 'object' || Array.isArray(tmpContextRoot))
			{
				continue;
			}
			for (const tmpEntity of Object.keys(tmpContextRoot))
			{
				const tmpBucket = tmpContextRoot[tmpEntity];
				if (tmpBucket && typeof tmpBucket === 'object' && !Array.isArray(tmpBucket) && Object.keys(tmpBucket).length < 1)
				{
					delete tmpContextRoot[tmpEntity];
				}
			}
			if (tmpContextKey !== null && Object.keys(tmpContextRoot).length < 1)
			{
				delete tmpTriggerNode[tmpContextKey];
			}
		}
		if (Object.keys(tmpTriggerNode).length < 1)
		{
			delete pDestination[tmpTrigger];
		}
	}
}

/** Why a removal did not stage a tombstone (or that it did). */
const TOMBSTONE_OUTCOME = Object.freeze(
	{
		Staged: 'staged',
		NoGUID: 'no-guid',
		Unpersisted: 'unpersisted',
		NotOwned: 'not-owned',
	});

/**
 * The rows a selector correlates to its selection, and so the only entity types a removal may delete. Each
 * selector declares its own (see `_collectRemovedDependentRecords`); this set is the assertion that none of
 * them ever hands the gate a reference record such as a Lab, Test or Material.
 */
const DOCUMENT_OWNED_ENTITIES = new Set([ 'TestInstance', 'TestInstanceSampleJoin', 'TestInstanceLabJoin', 'SampleLabJoin', 'SampleLineItemJoin', 'DocumentPolyJoin' ]);

/**
 * Stage a tombstone for a removed record.
 *
 * The record must be one of the rows a selector owns (`DOCUMENT_OWNED_ENTITIES`), and it must have been
 * written (`isPersistedGUID`): tombstoning something the server has never seen asks it to delete a phantom.
 *
 * This is the single entry point every removal route uses, so the Select2 and Picker families cannot
 * drift apart on what "removed" means.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @param {Record<string, any>} [pRecord] - (optional) The in-memory record.
 * @return {string} - One of `TOMBSTONE_OUTCOME`.
 */
function writeTombstoneIfDeletable(pPict, pContext, pEntity, pGUID, pRecord)
{
	if (!pEntity || typeof pGUID !== 'string' || pGUID.length < 1)
	{
		return TOMBSTONE_OUTCOME.NoGUID;
	}
	if (!DOCUMENT_OWNED_ENTITIES.has(pEntity))
	{
		return TOMBSTONE_OUTCOME.NotOwned;
	}
	// Asked before the leaves go, because a leaf a prior save wrote is one of the answers.
	const tmpPersisted = isPersistedGUID(pPict, pContext, pEntity, pGUID, pRecord);
	// The leaves go either way. A record the user removed has no staged intent left, and one staged for
	// a trigger would otherwise upsert the row back the next time the document reaches it.
	dropRecordLeaves(pPict, pEntity, pGUID);
	if (!tmpPersisted)
	{
		return TOMBSTONE_OUTCOME.Unpersisted;
	}
	return writeTombstone(pPict, pContext, pEntity, pGUID) ? TOMBSTONE_OUTCOME.Staged : TOMBSTONE_OUTCOME.NoGUID;
}

/**
 * Stage tombstones for a set of removed records.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {Array<{ Entity: string, Record: Record<string, any> }>} pRemovals - The removed records.
 * @return {{ Staged: Array<string>, Unpersisted: number, Refused: Array<string> }} - What happened, for the caller to log.
 */
function tombstoneRemovedRecords(pPict, pContext, pRemovals)
{
	const tmpResult = { Staged: [ ], Unpersisted: 0, Refused: [ ] };
	if (!Array.isArray(pRemovals))
	{
		return tmpResult;
	}
	for (const tmpRemoval of pRemovals)
	{
		const tmpEntity = tmpRemoval && tmpRemoval.Entity;
		const tmpRecord = tmpRemoval && tmpRemoval.Record;
		if (!tmpEntity || !tmpRecord || typeof tmpRecord !== 'object' || Array.isArray(tmpRecord))
		{
			continue;
		}
		const tmpGUID = tmpRecord[`GUID${tmpEntity}`];
		const tmpOutcome = writeTombstoneIfDeletable(pPict, pContext, tmpEntity, tmpGUID, tmpRecord);
		if (tmpOutcome === TOMBSTONE_OUTCOME.Staged)
		{
			tmpResult.Staged.push(`${tmpEntity}:${tmpGUID}`);
		}
		else if (tmpOutcome === TOMBSTONE_OUTCOME.Unpersisted)
		{
			tmpResult.Unpersisted++;
		}
		else if (tmpOutcome === TOMBSTONE_OUTCOME.NotOwned)
		{
			tmpResult.Refused.push(`${tmpEntity}:${tmpGUID}`);
		}
	}
	if (tmpResult.Refused.length > 0 && pPict && pPict.log && typeof pPict.log.warn === 'function')
	{
		pPict.log.warn(`ScopedEntityData: refused to delete ${tmpResult.Refused.length} record(s) the document does not own: ${tmpResult.Refused.join(', ')}.`);
	}
	return tmpResult;
}

/**
 * The entity a record names ITSELF as, from its `GUID<Entity>` fields.
 *
 * A record carries its own GUID alongside the GUIDs of the rows it points at — a
 * `TestInstanceSampleJoin` holds `GUIDTestInstanceSampleJoin`, `GUIDTestInstance` and `GUIDSample`.
 * The self field is the LONGEST such name: a join's entity name is built by concatenating the names
 * of what it joins, so it is always a superstring of the references beside it. `GUIDDocument` is
 * never a self field on these records, and a `_Dest_GUID<X>` sibling marks `GUID<X>` as a foreign-key
 * redirect rather than an identity.
 *
 * @param {Record<string, any>} pRecord - The candidate record.
 * @return {string} - The entity name, or '' when the object is not an entity record.
 */
function resolveRecordEntity(pRecord)
{
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return '';
	}
	let tmpEntity = '';
	for (const tmpField of Object.keys(pRecord))
	{
		if (!tmpField.startsWith('GUID') || tmpField.length < 5)
		{
			continue;
		}
		const tmpCandidate = tmpField.substring(4);
		if (tmpCandidate === 'Document' || Object.prototype.hasOwnProperty.call(pRecord, `_Dest_GUID${tmpCandidate}`))
		{
			continue;
		}
		if (typeof pRecord[tmpField] !== 'string' || pRecord[tmpField].length < 1)
		{
			continue;
		}
		if (tmpCandidate.length > tmpEntity.length)
		{
			tmpEntity = tmpCandidate;
		}
	}
	return tmpEntity;
}

/** The slots of a Test selection's triple that hold rows it owns, and the entity each holds. */
const TEST_INSTANCE_UNIT_SLOTS = [ [ 'TestInstance', 'TestInstance' ], [ 'TestInstanceSampleJoin', 'TestInstanceSampleJoin' ], [ 'DocumentPolyJoin', 'DocumentPolyJoin' ], [ '_TestInstanceLabJoin', 'TestInstanceLabJoin' ] ];

/**
 * The rows a Test selection owns: its TestInstance, the instance's sample join, the document's link to it, and the
 * per-test lab picker's lab join. Read from the triple's own slots, never by walking it, so the reference records
 * its pickers display (`IDLab__Record`, the Test) can never be taken for one of its rows.
 *
 * @param {Record<string, any>} pTriple - A `{ TestInstance, TestInstanceSampleJoin, DocumentPolyJoin, _TestInstanceLabJoin }` triple.
 * @return {Array<{ Entity: string, Record: Record<string, any> }>} - The owned rows the triple holds.
 */
function collectTestInstanceUnit(pTriple)
{
	const tmpUnit = [ ];
	if (!pTriple || typeof pTriple !== 'object' || Array.isArray(pTriple))
	{
		return tmpUnit;
	}
	for (const [ tmpSlot, tmpEntity ] of TEST_INSTANCE_UNIT_SLOTS)
	{
		const tmpRecord = pTriple[tmpSlot];
		if (tmpRecord && typeof tmpRecord === 'object' && !Array.isArray(tmpRecord))
		{
			tmpUnit.push({ Entity: tmpEntity, Record: tmpRecord });
		}
	}
	return tmpUnit;
}

/**
 * The rows the selectors in one table row own, asked of each selector rather than read off the row: the row also
 * holds the reference records its pickers display, and those are never a row delete's to remove.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Object} pView - The dynamic-form view owning the row.
 * @param {Record<string, any>} pGroup - The tabular group descriptor.
 * @param {number|string} pRowIndex - The row about to be deleted.
 * @return {Array<{ Entity: string, Record: Record<string, any> }>} - Every selector's owned rows.
 */
function collectRowSelectorRemovals(pPict, pView, pGroup, pRowIndex)
{
	const tmpRemovals = [ ];
	const tmpDescriptors = (pGroup && pGroup.supportingManifest && pGroup.supportingManifest.elementDescriptors) || { };
	if (!pView || typeof pView.getInputProviderList !== 'function')
	{
		return tmpRemovals;
	}
	for (const tmpInput of Object.values(tmpDescriptors))
	{
		for (const tmpProviderHash of pView.getInputProviderList(tmpInput) || [ ])
		{
			const tmpProvider = pPict && pPict.providers && pPict.providers[tmpProviderHash];
			if (tmpProvider && typeof tmpProvider._collectRemovedDependentRecords === 'function')
			{
				tmpRemovals.push(...tmpProvider._collectRemovedDependentRecords(pView, tmpInput, pRowIndex, new Set()));
			}
		}
	}
	return tmpRemovals;
}

/**
 * A persisted TestInstance as a Test selector adopts it on open, in the shape its triple builder later produces
 * (the project filled from the document when the row lacks one), so a load baseline taken before the builder runs
 * already matches what the first save projects.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Record<string, any>} pRow - The TestInstance row as the server returned it.
 * @return {Record<string, any>} - The adopted TestInstance record.
 */
function adoptTestInstance(pPict, pRow)
{
	const tmpInstance = { IDTestInstance: pRow.IDTestInstance, GUIDTestInstance: pRow.GUIDTestInstance, IDTest: pRow.IDTest, IDLab: pRow.IDLab, Name: pRow.Name };
	const tmpIDProject = (Number(pRow.IDProject) > 0) ? pRow.IDProject : pPict?.AppData?.DocumentData?.ReportData?.IDProject;
	if (Number(tmpIDProject) > 0)
	{
		tmpInstance.IDProject = tmpIDProject;
	}
	return tmpInstance;
}

/**
 * Split a TestInstance into the part that saves now and the TestResult that must wait for the
 * approval action. A test result is a reviewed outcome: it must not reach the row when the
 * document is merely saved. Shared by both Test inputs so the Select2 and Picker families cannot
 * disagree on result timing.
 *
 * @param {Record<string, any>} pTestInstance - The TestInstance record.
 * @return {{ Record: Record<string, any>, TestResult: any }} - The save-time record and the held-back result (undefined when there is none to defer).
 */
function splitDeferredTestResult(pTestInstance)
{
	if (!pTestInstance || typeof pTestInstance !== 'object' || !Object.prototype.hasOwnProperty.call(pTestInstance, 'TestResult'))
	{
		return { Record: pTestInstance, TestResult: undefined };
	}
	const tmpProjected = Object.assign({ }, pTestInstance);
	const tmpTestResult = tmpProjected.TestResult;
	delete tmpProjected.TestResult;
	if (tmpTestResult == null || tmpTestResult === '')
	{
		return { Record: tmpProjected, TestResult: undefined };
	}
	return { Record: tmpProjected, TestResult: tmpTestResult };
}

/**
 * Whether a deferred context is one a trigger can find again.
 *
 * It must name a trigger and exactly one action or status (`OnEnteringState.Approved`). The parts are
 * split on `.` here and the server looks the status up as a single key, so a status that contains one
 * would nest into a branch nothing reads.
 *
 * @param {Object} pPict - The pict instance, for the log line.
 * @param {string} pContext - The candidate context.
 * @return {string} - The context, or '' when it may not be used.
 */
function validateDeferredContext(pPict, pContext)
{
	const tmpParts = String(pContext || '').split('.').filter((pPart) => pPart.length > 0);
	if (tmpParts.length === 2 && DEFERRED_TRIGGERS.indexOf(tmpParts[0]) >= 0)
	{
		return tmpParts.join('.');
	}
	if (pPict && pPict.log && typeof pPict.log.warn === 'function')
	{
		pPict.log.warn(`ScopedEntityData: [${pContext}] is not a usable deferred context — it must name a trigger (${DEFERRED_TRIGGERS.join(' / ')}) and one action or status, with no dot inside it.`);
	}
	return '';
}

/**
 * Resolve the context a DEFERRED value (today: a TestInstance's TestResult) is applied under.
 * `PictForm.DeferredComprehensionContext` selects it; the default stages it for the approval
 * action, which is where a reviewed outcome belongs.
 *
 * @param {Object} pInput - The form input descriptor.
 * @param {string} [pDefaultContext] - The provider's default when the input declares none.
 * @return {string} - The comprehension context, or '' when neither the configured nor the default one is usable.
 */
function resolveDeferredContext(pInput, pDefaultContext)
{
	const tmpConfigured = pInput && pInput.PictForm && pInput.PictForm.DeferredComprehensionContext;
	if (typeof tmpConfigured === 'string' && tmpConfigured.length > 0)
	{
		return validateDeferredContext(pInput, tmpConfigured);
	}
	return validateDeferredContext(pInput, pDefaultContext || DEFAULT_DEFERRED_CONTEXT);
}

/**
 * Stage one field of a record for a workflow trigger to apply, at the moment the user changed it.
 *
 * The leaf is the form's standing intent for that field: the server copies it onto the row when the
 * document reaches the trigger and leaves it in place for the next time it does. It is written here,
 * at the edit, rather than re-derived on every save sweep — which is what makes a cleared value stage
 * as a clear, and what keeps a save where the user touched nothing from re-asserting it.
 *
 * A cleared value stages as a clear rather than dropping the leaf: no result is a valid outcome to
 * push, and dropping it would leave a previously applied result standing on the row.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The dotted trigger context, e.g. `OnEnteringState.Approved`.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @param {string} pField - The column being staged.
 * @param {any} pValue - The value the trigger should apply; `''` for a cleared one.
 * @return {boolean} - True when the value was staged.
 */
function stageDeferredValue(pPict, pContext, pEntity, pGUID, pField, pValue)
{
	if (!pEntity || !pField || typeof pGUID !== 'string' || pGUID.length < 1)
	{
		return false;
	}
	if (isProgrammaticLoad(pPict))
	{
		return false;
	}
	const tmpContext = validateDeferredContext(pPict, pContext);
	if (!tmpContext)
	{
		return false;
	}
	const tmpDestination = resolveDestination(pPict);
	if (!tmpDestination)
	{
		return false;
	}
	const tmpBucket = resolveEntityBucket(tmpDestination, tmpContext, pEntity);
	const tmpExisting = (tmpBucket[pGUID] && typeof tmpBucket[pGUID] === 'object' && !Array.isArray(tmpBucket[pGUID])) ? tmpBucket[pGUID] : { };
	const tmpRecord = Object.assign({ }, tmpExisting);
	tmpRecord[`GUID${pEntity}`] = pGUID;
	tmpRecord[pField] = (pValue === null || typeof pValue === 'undefined') ? '' : pValue;
	if (!writeRecord(pPict, tmpContext, pEntity, pGUID, tmpRecord))
	{
		return false;
	}
	dropSupersededDeferredLeaf(pPict, tmpDestination, tmpContext, pEntity, pGUID);
	return true;
}

/**
 * Take a record's leaf out of the deprecated deferred trigger once it is staged under another one.
 *
 * A document that carries both applies the deprecated leaf on every approval ACTION and the current
 * one when the document enters the state — two writes from one intent, the earlier of them at the
 * wrong moment. The form that just staged the value owns it, so the old copy goes with it.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Record<string, any>} pDestination - The comprehension destination.
 * @param {string} pContext - The context just staged under.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @return {number} - How many superseded leaves were dropped.
 */
function dropSupersededDeferredLeaf(pPict, pDestination, pContext, pEntity, pGUID)
{
	if (String(pContext).split('.')[0] === LEGACY_DEFERRED_TRIGGER)
	{
		return 0;
	}
	const tmpLegacyRoot = pDestination[LEGACY_DEFERRED_TRIGGER];
	if (!tmpLegacyRoot || typeof tmpLegacyRoot !== 'object' || Array.isArray(tmpLegacyRoot))
	{
		return 0;
	}
	let tmpDropped = 0;
	for (const tmpKey of Object.keys(tmpLegacyRoot))
	{
		const tmpBucket = tmpLegacyRoot[tmpKey] && tmpLegacyRoot[tmpKey][pEntity];
		if (tmpBucket && typeof tmpBucket === 'object' && !Array.isArray(tmpBucket) && Object.prototype.hasOwnProperty.call(tmpBucket, pGUID))
		{
			delete tmpBucket[pGUID];
			tmpDropped++;
		}
	}
	if (tmpDropped > 0)
	{
		scrubEmptyContainers(pDestination);
		if (pPict && pPict.log && typeof pPict.log.info === 'function')
		{
			pPict.log.info(`ScopedEntityData: moved ${pEntity} ${pGUID} off the deprecated ${LEGACY_DEFERRED_TRIGGER} trigger and onto ${pContext}.`);
		}
	}
	return tmpDropped;
}

/**
 * Project a TestInstance into a save-time comprehension target with its TestResult held back.
 *
 * A test result is a reviewed outcome: it must not reach the TestInstance row when the document is
 * merely saved, only when the workflow reaches the trigger it was staged for. Everything else on the
 * record still saves normally, so the row (and its joins) are created on save as before.
 *
 * The result itself is staged by `stageDeferredValue` at the moment the user sets it. A sweep can only
 * write what the form holds, which cannot tell a result the user cleared from one that was never
 * entered, and would re-assert a standing leaf on every save the document makes.
 *
 * Shared by both Test inputs (the Select2 `MaterialsTestSelector` and the PSP
 * `MaterialsTestPicker`) so the two families cannot disagree on when a result reaches the row.
 *
 * @param {Record<string, any>} pTestInstance - The TestInstance record.
 * @param {Record<string, any>} pTargetEntityData - The save-time `<Entity>.<GUID>` target.
 * @return {boolean} - False when the record could not be projected (the caller warns).
 */
function projectTestInstanceDeferringResult(pTestInstance, pTargetEntityData)
{
	const tmpGUID = pTestInstance && pTestInstance.GUIDTestInstance;
	if (!tmpGUID || !pTargetEntityData || typeof pTargetEntityData !== 'object' || Array.isArray(pTargetEntityData))
	{
		return false;
	}
	if (!pTargetEntityData.TestInstance || typeof pTargetEntityData.TestInstance !== 'object' || Array.isArray(pTargetEntityData.TestInstance))
	{
		pTargetEntityData.TestInstance = { };
	}
	pTargetEntityData.TestInstance[tmpGUID] = splitDeferredTestResult(pTestInstance).Record;
	return true;
}

/**
 * Whether a row's `Deleted` column marks it soft-deleted.
 *
 * @param {any} pValue - The `Deleted` value as a read returned it.
 * @return {boolean} - True when set.
 */
function isSoftDeletedFlag(pValue)
{
	return pValue === 1 || pValue === true || pValue === '1' || pValue === 'true';
}

/**
 * Which held joins name a row that exists and is soft-deleted, and the selection left without them.
 *
 * Only positive evidence drops a join: its GUID read back with `includeDeleted`, and every row that came
 * back deleted. A live row, a missing row or a failed read keeps the join. A selected value is removed
 * only when no kept join still carries it.
 *
 * The joins are read once, when the call is made, and never modified. They can change while the reads
 * are in flight, so a caller that writes the outcome back applies `DeletedValues` to the joins and value
 * it holds at that point (`applySoftDeletedDrop`) instead of writing `Joins` and `Value`.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pEntity - The join entity, e.g. `SampleLineItemJoin`.
 * @param {string} pValueField - The column the selection lists, e.g. `IDLineItem`.
 * @param {Array<Record<string, any>>} pJoins - The held joins.
 * @param {any} pValue - The current selection value (comma-separated ids).
 * @param {(pResult: { Dropped: Array<string>, DeletedValues: Record<string, string>, Checked: Array<string>, Joins: Array<Record<string, any>>, Value: string }) => void} fCallback - Called once.
 *   `Dropped` lists the deleted GUIDs, `DeletedValues` maps each to the `pValueField` its row held,
 *   `Checked` lists the GUIDs read, and `Joins` / `Value` are the call-time joins and value with the drop applied.
 */
function resolveSoftDeletedJoins(pPict, pEntity, pValueField, pJoins, pValue, fCallback)
{
	const tmpGUIDField = `GUID${pEntity}`;
	const tmpJoins = Array.isArray(pJoins) ? pJoins.slice() : [ ];
	const tmpValue = (pValue === null || typeof pValue === 'undefined') ? '' : String(pValue);
	const tmpCandidates = tmpJoins.filter((pJoin) => pJoin && typeof pJoin[tmpGUIDField] === 'string' && pJoin[tmpGUIDField].length > 0);
	const tmpChecked = tmpCandidates.map((pJoin) => pJoin[tmpGUIDField]);
	const tmpRestClient = pPict && pPict.HeadlightRestClient;
	const fUnchanged = (pChecked) => fCallback({ Dropped: [ ], DeletedValues: { }, Checked: pChecked, Joins: tmpJoins, Value: tmpValue });
	if (tmpCandidates.length < 1 || !tmpRestClient || typeof tmpRestClient.getJSON !== 'function')
	{
		return fUnchanged([ ]);
	}
	/** @type {Record<string, string>} */
	const tmpDeletedValues = { };
	let tmpRemaining = tmpCandidates.length;
	const fSettle = () =>
	{
		--tmpRemaining;
		if (tmpRemaining > 0)
		{
			return;
		}
		const tmpDropped = Object.keys(tmpDeletedValues);
		if (tmpDropped.length < 1)
		{
			return fUnchanged(tmpChecked);
		}
		const tmpApplied = applySoftDeletedDrop(pEntity, pValueField, tmpJoins, tmpValue, { DeletedValues: tmpDeletedValues, Checked: tmpChecked });
		return fCallback({ Dropped: tmpDropped, DeletedValues: tmpDeletedValues, Checked: tmpChecked, Joins: tmpApplied.Joins, Value: tmpApplied.Value });
	};
	for (const tmpJoin of tmpCandidates)
	{
		const tmpGUID = tmpJoin[tmpGUIDField];
		try
		{
			tmpRestClient.getJSON(`${pEntity}s/FilteredTo/FBV~${tmpGUIDField}~EQ~${encodeURIComponent(tmpGUID)}/0/5?includeDeleted=true`, (pError, pRows) =>
			{
				if (!pError && Array.isArray(pRows) && pRows.length > 0 && pRows.every((pRow) => pRow && isSoftDeletedFlag(pRow.Deleted)))
				{
					const tmpRowValue = pRows.map((pRow) => pRow[pValueField]).find((pRowValue) => pRowValue !== null && typeof pRowValue !== 'undefined' && pRowValue !== '');
					tmpDeletedValues[tmpGUID] = String(tmpRowValue ?? tmpJoin[pValueField] ?? '');
				}
				fSettle();
			});
		}
		catch (pError)
		{
			fSettle();
		}
	}
}

/**
 * A soft-delete drop (`resolveSoftDeletedJoins`) applied to the joins and selection an input holds now.
 *
 * A join goes when its GUID was read back deleted, or when it has no row id, was not among the joins read,
 * and lists a value a deleted row held: the same link minted again under another GUID while the reads were
 * in flight, which no user action asked for. A join with a row id, or one read back live, stays. A value
 * goes when a deleted row held it and no kept join lists it. Once applied, applying it again changes nothing.
 *
 * @param {string} pEntity - The join entity, e.g. `SampleLineItemJoin`.
 * @param {string} pValueField - The column the selection lists, e.g. `IDLineItem`.
 * @param {any} pJoins - The joins held now.
 * @param {any} pValue - The selection value held now (comma-separated ids).
 * @param {{ DeletedValues: Record<string, string>, Checked?: Array<string> }} pResolved - From
 *   `resolveSoftDeletedJoins`: per deleted GUID the `pValueField` its row held, and the GUIDs that were read.
 * @return {{ Changed: boolean, ValueChanged: boolean, Removed: Array<string>, Joins: Array<Record<string, any>>, Value: string }}
 *   `Removed` lists the GUIDs of the joins taken out. When nothing changes, `Joins` is `pJoins` itself (or
 *   `[ ]`) and `Value` is `pValue` as a string.
 */
function applySoftDeletedDrop(pEntity, pValueField, pJoins, pValue, pResolved)
{
	const tmpGUIDField = `GUID${pEntity}`;
	const tmpIDField = `ID${pEntity}`;
	const tmpDeletedValueMap = (pResolved && pResolved.DeletedValues) || { };
	const tmpDeletedGUIDs = new Set(Object.keys(tmpDeletedValueMap));
	const tmpDeletedValues = new Set(Object.values(tmpDeletedValueMap).map(String).filter((pEntry) => pEntry.length > 0));
	const tmpChecked = new Set((pResolved && Array.isArray(pResolved.Checked)) ? pResolved.Checked : [ ]);
	const tmpJoins = Array.isArray(pJoins) ? pJoins : [ ];
	const tmpValue = Array.isArray(pValue) ? pValue.join(',') : String(pValue ?? '');
	const isReminted = (pJoin) => !tmpChecked.has(pJoin[tmpGUIDField]) && !(Number(pJoin[tmpIDField]) > 0) && tmpDeletedValues.has(String(pJoin[pValueField]));
	const isDropped = (pJoin) => !!(pJoin && typeof pJoin === 'object') && (tmpDeletedGUIDs.has(pJoin[tmpGUIDField]) || isReminted(pJoin));
	const tmpKept = tmpJoins.filter((pJoin) => !isDropped(pJoin));
	const tmpRemoved = tmpJoins.filter(isDropped).map((pJoin) => pJoin[tmpGUIDField]);
	const tmpKeptValues = new Set(tmpKept.map((pJoin) => String(pJoin && pJoin[pValueField])));
	const tmpParts = tmpValue.split(',').map((pPart) => pPart.trim()).filter((pPart) => pPart.length > 0);
	const tmpNextParts = tmpParts.filter((pPart) => !tmpDeletedValues.has(pPart) || tmpKeptValues.has(pPart));
	const tmpValueChanged = tmpNextParts.length !== tmpParts.length;
	if (tmpRemoved.length < 1 && !tmpValueChanged)
	{
		return { Changed: false, ValueChanged: false, Removed: [ ], Joins: tmpJoins, Value: tmpValue };
	}
	return { Changed: true, ValueChanged: tmpValueChanged, Removed: tmpRemoved, Joins: tmpKept, Value: tmpValueChanged ? tmpNextParts.join(',') : tmpValue };
}

/**
 * The held records an input may keep once its selection has narrowed: those whose `pValueField` is
 * still listed in `pValue`. The records back the value (an autofill can re-derive the value from them),
 * so a record left behind for a value that was dropped puts that value back.
 *
 * @param {any} pRecords - The held records: an array for a Multiple input, else a single record.
 * @param {string} pValueField - The field the selection lists, e.g. `IDLineItem`.
 * @param {any} pValue - The selection value (comma-separated ids).
 * @return {any} - The kept records, in the same shape; a dropped single record becomes `{ }`. When nothing
 *   is removed the very same value comes back, so a caller can skip the write.
 */
function pruneRecordsToValue(pRecords, pValueField, pValue)
{
	const tmpKept = new Set(String(pValue ?? '').split(',').map((pPart) => pPart.trim()).filter((pPart) => pPart.length > 0));
	const isKept = (pRecord) => !!(pRecord && typeof pRecord === 'object' && tmpKept.has(String(pRecord[pValueField])));
	if (Array.isArray(pRecords))
	{
		const tmpFiltered = pRecords.filter(isKept);
		return (tmpFiltered.length === pRecords.length) ? pRecords : tmpFiltered;
	}
	if (pRecords && typeof pRecords === 'object')
	{
		return isKept(pRecords) ? pRecords : { };
	}
	return pRecords;
}

/**
 * Whether a record names a sample, by server id or by the GUID of one the same save creates. Answers
 * for a selected sample and for a join that hangs from one alike.
 *
 * @param {any} pRecord - The sample, or a join carrying `IDSample` / `GUIDSample`.
 * @return {boolean} - True when a sample is named.
 */
function referencesSample(pRecord)
{
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return false;
	}
	if (Number(pRecord.IDSample) > 0)
	{
		return true;
	}
	return (typeof pRecord.GUIDSample === 'string' && pRecord.GUIDSample.length > 0);
}

/**
 * @param {any} pValue - The value to test.
 * @return {boolean} - True for a string with something other than whitespace in it.
 */
function isNonEmptyString(pValue)
{
	return (typeof pValue === 'string' && pValue.trim().length > 0);
}

/**
 * Why a projected leaf must not ship, or '' when it may.
 *
 * The applier stamps the key onto the record as its `GUID<Entity>`, so a leaf keyed by no usable GUID
 * is inserted as a row nothing can address again. A `DocumentPolyJoin` with no `RemoteType`, or with
 * nothing naming the record it joins, is inserted as a row with no parent. A tombstone carries only its
 * GUID and the delete, so the parent rule does not apply to one.
 *
 * @param {string} pEntity - The entity bucket the leaf sits in.
 * @param {string} pGUID - The key the leaf sits under.
 * @param {Record<string, any>} pRecord - The leaf.
 * @return {string} - The reason, or '' when the leaf may be emitted.
 */
function getUnemittableReason(pEntity, pGUID, pRecord)
{
	if (!isNonEmptyString(pGUID) || pGUID === 'undefined' || pGUID === 'null')
	{
		return `it is keyed by '${pGUID}'`;
	}
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return '';
	}
	if (pEntity !== 'DocumentPolyJoin' || isSoftDeletedFlag(pRecord.Deleted))
	{
		return '';
	}
	const tmpRemoteType = pRecord.RemoteType;
	if (!isNonEmptyString(tmpRemoteType))
	{
		return 'it has no RemoteType';
	}
	if (Number(pRecord.IDRemote) > 0)
	{
		return '';
	}
	if (pRecord[`_Dest_GUID${tmpRemoteType}`] && isNonEmptyString(pRecord[`GUID${tmpRemoteType}`]))
	{
		return '';
	}
	if (pRecord[`_Dest__GUID${tmpRemoteType}`] && isNonEmptyString(pRecord[`_GUID${tmpRemoteType}`]))
	{
		return '';
	}
	return `it names no ${tmpRemoteType} to join`;
}

module.exports =
{
	DEFAULT_CONTEXT,
	DEFAULT_DEFERRED_CONTEXT,
	TRIGGER_CONTEXT_DEPTH,
	DEFERRED_TRIGGERS,
	LEGACY_DEFERRED_TRIGGER,
	DOCUMENT_ENTITY_DATA_ADDRESS,
	DOCUMENT_RECORD_ADDRESS,
	ENTITY_DATA_BASELINE_ADDRESS,
	validateDeferredContext,
	stageDeferredValue,
	forEachContextRoot,
	scrubEmptyContainers,
	TOMBSTONE_OUTCOME,
	DOCUMENT_OWNED_ENTITIES,
	readFlagValue,
	isEnabled,
	ACTIVE_MARKER,
	markActive,
	isMarkedActive,
	noteProjectionRoot,
	takeNotedProjectionRoots,
	resolveContext,
	resolveDeferredContext,
	isTransientFormField,
	sanitizeRecord,
	isUnresolvedIdentityField,
	resolveDestination,
	resolveContextRoot,
	resolveEntityBucket,
	sanitizeContextRoot,
	BASELINE_SWEEP_MARKER,
	beginBaselineSweep,
	getBaselineSweepTarget,
	endBaselineSweep,
	writeRecord,
	writeTombstone,
	writeTombstoneIfDeletable,
	tombstoneRemovedRecords,
	dropRecordLeaves,
	isPersistedGUID,
	resolveRecordEntity,
	collectTestInstanceUnit,
	collectRowSelectorRemovals,
	adoptTestInstance,
	resolveSoftDeletedJoins,
	applySoftDeletedDrop,
	pruneRecordsToValue,
	beginProgrammaticLoad,
	endProgrammaticLoad,
	isProgrammaticLoad,
	splitDeferredTestResult,
	projectTestInstanceDeferringResult,
	referencesSample,
	getUnemittableReason,
};
